import 'globalize/dist/globalize/number';
import 'globalize/dist/globalize/currency';
import 'globalize/dist/globalize/plural';
import * as Logger from '@thoughtspot/logger';
import { getNarrowCurrencyCodes, isNarrowCurrencySymbolEnabled, isTrimCurrencySpacingEnabled, } from '@thoughtspot/session-service';
import { numberFormats, strings } from '@thoughtspot/translations';
import Globalize from 'globalize/dist/globalize';
import _ from 'lodash';
import { CategoryType, NegativeValueFormat, Unit } from './types/prism';
const _logger = Logger.create('number-format');
let cachedCurrenciesJson;
let currentLocale;
let currentCurrencyFormat;
let cldrCurrencyDataLoader;
let cldrNumbersDataLoader;
let supplementalCurrencyDataJson;
let UNITS_TO_DIVIDING_FACTOR = {
    [Unit.None]: 1,
    [Unit.Thousands]: 1000,
    [Unit.Million]: 1000 * 1000,
    [Unit.Billion]: 1000 * 1000 * 1000,
    [Unit.Trillion]: 1000 * 1000 * 1000 * 1000,
};
let UNITS_TO_SUFFIX = {
    [Unit.None]: '',
    [Unit.Thousands]: 'K',
    [Unit.Million]: 'M',
    [Unit.Billion]: 'B',
    [Unit.Trillion]: 'T',
};
const CLDR_OVERRIDE_MAP = {
    'zh-TW': 'zh-Hant',
};
const CLDR_COUNTRY_CODE_OVERRIDE_MAP = {
    Hant: 'TW',
};
export const initUnits = () => {
    const dividingFactor = _.get(numberFormats, 'numberFormat.dividingFactor', null);
    const numberFormatSuffix = _.get(numberFormats, 'numberFormat.suffix', null);
    if (_.isNil(dividingFactor) || _.isNil(numberFormatSuffix)) {
        _logger.warn('Number Format Strings are not initialized. Using the default en_US locale');
        return;
    }
    UNITS_TO_DIVIDING_FACTOR = {
        [Unit.None]: 1,
        [Unit.Thousands]: parseInt(numberFormats.numberFormat.dividingFactor.Thousand, 10),
        [Unit.Million]: parseInt(numberFormats.numberFormat.dividingFactor.Million, 10),
        [Unit.Billion]: parseInt(numberFormats.numberFormat.dividingFactor.Billion, 10),
        [Unit.Trillion]: parseInt(numberFormats.numberFormat.dividingFactor.Trillion, 10),
    };
    UNITS_TO_SUFFIX = {
        [Unit.None]: '',
        [Unit.Thousands]: numberFormats.numberFormat.suffix.Thousand,
        [Unit.Million]: numberFormats.numberFormat.suffix.Million,
        [Unit.Billion]: numberFormats.numberFormat.suffix.Billion,
        [Unit.Trillion]: numberFormats.numberFormat.suffix.Trillion,
    };
};
const UNIT_TO_NEW_CHART_SUFFIX_KEY = {
    [Unit.Thousands]: 'Thousand',
    [Unit.Million]: 'Million',
    [Unit.Billion]: 'Billion',
    [Unit.Trillion]: 'Trillion',
};
const ABBREVIATED_UNITS = [
    Unit.Thousands,
    Unit.Million,
    Unit.Billion,
    Unit.Trillion,
];
export const getNewChartUnitSuffixOverrides = () => {
    const newChartSuffix = _.get(strings, 'numberFormat.newChart.suffix', {});
    const overrides = {};
    ABBREVIATED_UNITS.forEach(unit => {
        const suffix = newChartSuffix[UNIT_TO_NEW_CHART_SUFFIX_KEY[unit]];
        if (suffix && suffix !== UNITS_TO_SUFFIX[unit]) {
            overrides[unit] = suffix;
        }
    });
    return overrides;
};
const getCountryCode = (locale) => {
    let parts = locale.split('_');
    if (parts.length === 2) {
        return parts[1];
    }
    parts = locale.split('-');
    if (parts.length === 2) {
        return parts[1];
    }
    return locale;
};
const getDefaultCurrencyCode = () => {
    if (currentCurrencyFormat)
        return currentCurrencyFormat;
    const countryCode = getCountryCode(currentLocale);
    const updatedCountryCode = CLDR_COUNTRY_CODE_OVERRIDE_MAP[countryCode] ?? countryCode;
    const regionalCurrencyDetails = supplementalCurrencyDataJson.supplemental.currencyData.region[updatedCountryCode.toUpperCase()];
    if (_.isNil(regionalCurrencyDetails)) {
        return null;
    }
    return Object.keys(_.last(regionalCurrencyDetails))[0];
};
const sanitizeFormat = (format) => {
    let sanitizedFormat = format.replace(/#\./, '0.');
    if (!sanitizedFormat.includes('.')) {
        sanitizedFormat = sanitizedFormat.replace(/#(%?)$/, '0$1');
    }
    return sanitizedFormat;
};
const isValidNumericString = num => {
    return !isNaN(parseFloatNumber(num));
};
const combineCurrencyAndFormatPattern = (currencyFormattedValue, businessFormattedValue) => {
    let currencyPrefix = '';
    let currencySuffix = '';
    for (let i = 0; i < currencyFormattedValue.length; i++) {
        const c = currencyFormattedValue.charAt(i);
        if (!isValidNumericString(c)) {
            currencyPrefix += c;
        }
        else {
            break;
        }
    }
    for (let i = currencyFormattedValue.length - 1; i >= 0; i--) {
        const c = currencyFormattedValue.charAt(i);
        if (!isValidNumericString(c)) {
            currencySuffix = c + currencySuffix;
        }
        else {
            break;
        }
    }
    if (currencySuffix) {
        const endsWithNumShorteningSymbol = !isValidNumericString(businessFormattedValue.slice(-1));
        if (endsWithNumShorteningSymbol && currencySuffix.charAt(0) !== ' ') {
            currencySuffix = ` ${currencySuffix}`;
        }
        return businessFormattedValue + currencySuffix;
    }
    if (currencyPrefix) {
        const businessFormattedValueCharLength = businessFormattedValue.length;
        if (businessFormattedValue.charAt(0) === '-') {
            businessFormattedValue = businessFormattedValue.slice(1, businessFormattedValueCharLength);
        }
        else {
            const startsWithNumShorteningSymbol = !isValidNumericString(businessFormattedValue.charAt(0));
            if (currencyPrefix.slice(-1) !== ' ' &&
                startsWithNumShorteningSymbol) {
                currencyPrefix += ' ';
            }
        }
        return currencyPrefix + businessFormattedValue;
    }
    return businessFormattedValue;
};
async function initialize(locale, inputSupplementalLikelySubtags, inputSupplementalNumberingSystem, inputSupplementalCurrencyDataJson, inputSupplementalPluralJson, inputCldrCurrencyDataLoader, inputCldrNumbersDataLoader, currencyFormat = null) {
    Globalize.load(inputSupplementalLikelySubtags, inputSupplementalNumberingSystem, inputSupplementalCurrencyDataJson, inputSupplementalPluralJson);
    supplementalCurrencyDataJson = inputSupplementalCurrencyDataJson;
    cldrCurrencyDataLoader = inputCldrCurrencyDataLoader;
    cldrNumbersDataLoader = inputCldrNumbersDataLoader;
    currentCurrencyFormat = currencyFormat;
    await setLocale(locale);
}
const setLocale = async (locale) => {
    const previousLanguage = _.get(Globalize, 'cldr.attributes.minLanguageId');
    const overridePrevLanguage = CLDR_OVERRIDE_MAP[previousLanguage] ?? previousLanguage;
    Globalize.locale(locale);
    const languageId = _.get(Globalize, 'cldr.attributes.minLanguageId');
    const overrideLanguageId = CLDR_OVERRIDE_MAP[languageId] ?? languageId;
    if (overridePrevLanguage !== overrideLanguageId) {
        const localeJsons = await Promise.all([
            cldrCurrencyDataLoader(overrideLanguageId),
            cldrNumbersDataLoader(overrideLanguageId),
        ]);
        cachedCurrenciesJson = localeJsons[0];
        Globalize.load(localeJsons[0], localeJsons[1]);
        Globalize.locale(locale);
    }
    currentLocale = locale;
    initUnits();
};
const shortFormatter = (number, defaultDecimal = 2) => {
    if (!number) {
        return String(number);
    }
    return safeNumberFormat({
        compact: 'short',
        maximumFractionDigits: defaultDecimal,
    }, number);
};
export const removeLiterals = parts => parts
    .filter(part => part.type !== 'literal')
    .map(part => part.value)
    .join('');
export const currencyFormatter = (currencyCode, options, outputAsPart) => outputAsPart
    ? Globalize.currencyToPartsFormatter(currencyCode, options)
    : Globalize.currencyFormatter(currencyCode, options);
const getFormattedCurrency = (currencyCode, options, num) => {
    const shouldTrimSpace = isTrimCurrencySpacingEnabled();
    const formatter = currencyFormatter(currencyCode, options, shouldTrimSpace);
    const formattedValue = formatter(num);
    return shouldTrimSpace
        ? removeLiterals(formattedValue)
        : formattedValue;
};
const formatCurrency = (num, { nDecimal, currencyCode = getDefaultCurrencyCode(), compact, formatPattern, } = {}) => {
    const shouldTrimSpace = isTrimCurrencySpacingEnabled();
    const spaceValue = shouldTrimSpace ? '' : ' ';
    if (_.isNull(currencyCode) || _.isEmpty(currencyCode)) {
        _logger.error('Invalid or empty currency code', currencyCode);
        return num;
    }
    currencyCode = _.upperCase(currencyCode);
    const options = {
        style: 'symbol',
        minimumFractionDigits: nDecimal || 0,
        maximumFractionDigits: nDecimal || 2,
    };
    let isISOCurrencyCode = false;
    getSupportedCurrencyISOCodes().map(currencyISOCode => {
        if (currencyCode === currencyISOCode) {
            isISOCurrencyCode = true;
            if (isNarrowCurrencySymbolEnabled()) {
                if (getNarrowCurrencyCodes().includes(currencyISOCode))
                    options.symbolForm = 'narrow';
            }
        }
    });
    if (compact) {
        options.compact = compact;
    }
    else if (formatPattern) {
        const currencyFormattedVal = isISOCurrencyCode
            ? getFormattedCurrency(currencyCode, options, num)
            : `${currencyCode}${spaceValue}${num}`;
        const formattedVal = defaultFormatter(num, {
            nDecimal,
            formatPattern,
        });
        return combineCurrencyAndFormatPattern(currencyFormattedVal, formattedVal);
    }
    return isISOCurrencyCode
        ? getFormattedCurrency(currencyCode, options, num)
        : `${currencyCode}${spaceValue}${num}`;
};
const defaultFormatter = (num, { nDecimal, formatPattern, isPercent, isDouble } = {}) => {
    const options = {
        style: isPercent
            ? numberFormatStyle.PERCENT
            : numberFormatStyle.DECIMAL,
    };
    if (!_.isUndefined(nDecimal)) {
        options.minimumFractionDigits = nDecimal || 0;
        options.maximumFractionDigits = nDecimal || (isDouble ? 2 : 0);
    }
    else if (formatPattern) {
        let sanitizedPattern = sanitizeFormat(formatPattern);
        if (isPercent && sanitizedPattern.indexOf('%') < 0) {
            sanitizedPattern += '%';
        }
        if (validateNumberFormat(sanitizedPattern)) {
            options.raw = sanitizedPattern;
        }
    }
    return safeNumberFormat(options, num);
};
const numberFormatStyle = {
    PERCENT: 'percent',
    DECIMAL: 'decimal',
};
const parseFloatNumber = (numString, options) => {
    return Globalize.numberParser(options)(numString);
};
const validateNumberFormat = (format) => {
    try {
        Globalize.numberFormatter({ raw: sanitizeFormat(format) })(123);
    }
    catch (e) {
        _logger.log('Format pattern validator error', format, e);
        return false;
    }
    return true;
};
const getSupportedCurrencyISOCodes = () => {
    const languageId = _.get(Globalize, 'cldr.attributes.minLanguageId', 'en');
    const overrideLanguageId = CLDR_OVERRIDE_MAP[languageId] ?? languageId;
    const currencies = cachedCurrenciesJson?.main[overrideLanguageId].numbers.currencies;
    if (_.isNil(currencies))
        return [];
    return Object.keys(currencies).filter(symbol => {
        return currencies[symbol]?.displayName.indexOf('(') < 0;
    });
};
function safeNumberFormat(format, num) {
    try {
        return Globalize.numberFormatter(format)(num);
    }
    catch (e) {
        if (Math.abs(num) < 1e-7) {
            return '0';
        }
        return String(num);
    }
}
const PROTO_TO_UNITS = {
    1: Unit.None,
    2: Unit.Thousands,
    3: Unit.Million,
    4: Unit.Billion,
    5: Unit.Trillion,
    6: Unit.Auto,
};
const PROTO_TO_CATEGORY = {
    1: CategoryType.Number,
    2: CategoryType.Percentage,
    3: CategoryType.Currency,
    4: CategoryType.Custom,
};
const PROTO_TO_NEGATIVE_VALUE_FORMAT = {
    1: NegativeValueFormat.PrefixDash,
    2: NegativeValueFormat.SuffixDash,
    3: NegativeValueFormat.BracesNodash,
};
export { initialize, setLocale, shortFormatter, isValidNumericString, formatCurrency, defaultFormatter, numberFormatStyle, parseFloatNumber, validateNumberFormat, getSupportedCurrencyISOCodes, getDefaultCurrencyCode, sanitizeFormat, safeNumberFormat, PROTO_TO_CATEGORY, PROTO_TO_NEGATIVE_VALUE_FORMAT, PROTO_TO_UNITS, UNITS_TO_SUFFIX, UNITS_TO_DIVIDING_FACTOR, CLDR_OVERRIDE_MAP, };
//# sourceMappingURL=number-format.js.map